import { useCallback, useRef } from 'react';
import AcrossTabs from 'across-tabs';
import { convertToCleanJson } from '~/utils/json.utils';
import {
    IncomingMessages,
    OutgoingMessages,
    parseMessage
} from '~/common/CrossTabCommunication/crossTablogin';
import useAnalytics from '~/common/useAnalytics';
import { useAuthenticatedGlobalContext } from '~/stores/useAuthenticatedGlobalContext';

const useCXTabAuthentication = () => {
    const connectedTabManager = useRef(null);
    const { track } = useAnalytics();
    const {
        userStore: { currUser }
    } = useAuthenticatedGlobalContext();

    const handleChildTabMessage = useCallback(
        ({ action, id }) => {
            if (
                action === OutgoingMessages.requestAuthentication &&
                connectedTabManager.current
            ) {
                connectedTabManager.current.broadCastTo(
                    id,
                    JSON.stringify({
                        action: IncomingMessages.authentication,
                        user: convertToCleanJson(currUser)
                    })
                );
            }
        },
        [currUser]
    );

    const initConnectTabManager = useCallback(
        async (flowUrl: string) => {
            connectedTabManager.current = new AcrossTabs.Parent({
                onChildCommunication: data => {
                    const tabMessage = parseMessage(data);

                    if (!tabMessage) {
                        return;
                    }

                    handleChildTabMessage(tabMessage);
                },
                origin: new URL(flowUrl).origin
            });
        },
        [handleChildTabMessage]
    );

    const goToClientExperience = useCallback(
        async (flowUrl: string, source?: string) => {
            if (!connectedTabManager.current || !window.top.opener) {
                await initConnectTabManager(flowUrl);
            }

            connectedTabManager.current.openNewTab({
                url: flowUrl,
                windowName: '_blank'
            });

            if (source) {
                track('edit client response', { action: 'click', source });
            }
        },
        [initConnectTabManager, track]
    );

    return goToClientExperience;
};

export default useCXTabAuthentication;
